/* eslint-disable camelcase */
import isEmpty from 'lodash/isEmpty';

import { BOX_CONDITIONS, SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import { getFirstLowestPriceVariants } from 'shared/utils/getFirstLowestPriceVariants';
import { IEventAttributes } from 'tracking/types/Event';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';

export const getSelectedSizeVariant = (variants, selectedSize) =>
  variants.find((variant) => variant?.sizeOption?.value === selectedSize);

export const sendBuyBarTrackingEvent = ({
  eventName,
  expanded = false,
  isUnderRetail = false,
  isApparelProduct = false,
  productVariants,
  retailPrice,
  slug,
  section,
  size,
  sku,
  clientDedupID = '',
  service_name,
}: any) => {
  const lowestPricedNewGoodBoxCondition = getSelectedSizeVariant(
    getFirstLowestPriceVariants(
      productVariants,
      [SHOE_CONDITIONS.NEW_NO_DEFECTS],
      [BOX_CONDITIONS.GOOD_CONDITION],
    ),
    size,
  );
  const lowestPricedNewBadBoxCondition = getSelectedSizeVariant(
    getFirstLowestPriceVariants(
      productVariants,
      [SHOE_CONDITIONS.NEW_NO_DEFECTS],
      [BOX_CONDITIONS.MISSING_LID, BOX_CONDITIONS.BADLY_DAMAGED, BOX_CONDITIONS.NO_ORIGINAL_BOX],
    ),
    size,
  );
  const lowestPricedNewNoBoxCondition = getSelectedSizeVariant(
    getFirstLowestPriceVariants(
      productVariants,
      [SHOE_CONDITIONS.NEW_NO_DEFECTS],
      [BOX_CONDITIONS.NO_ORIGINAL_BOX],
    ),
    size,
  );
  const lowestPricedNewMissingLidCondition = getSelectedSizeVariant(
    getFirstLowestPriceVariants(
      productVariants,
      [SHOE_CONDITIONS.NEW_NO_DEFECTS],
      [BOX_CONDITIONS.MISSING_LID],
    ),
    size,
  );
  const lowestPricedBadBoxCondition = getSelectedSizeVariant(
    getFirstLowestPriceVariants(
      productVariants,
      [],
      [BOX_CONDITIONS.MISSING_LID, BOX_CONDITIONS.BADLY_DAMAGED, BOX_CONDITIONS.NO_ORIGINAL_BOX],
    ),
    size,
  );
  const lowestPricedNewWithDefects = getSelectedSizeVariant(
    getFirstLowestPriceVariants(productVariants, [SHOE_CONDITIONS.NEW_WITH_DEFECTS]),
    size,
  );
  const instantShipVariant = getSelectedSizeVariant(
    getFirstLowestPriceVariants(
      productVariants,
      [SHOE_CONDITIONS.NEW_NO_DEFECTS],
      [BOX_CONDITIONS.GOOD_CONDITION],
      true,
    ),
    size,
  );
  const lowestPricedUsedVariant = getSelectedSizeVariant(
    getFirstLowestPriceVariants(productVariants, [SHOE_CONDITIONS.USED]),
    size,
  );
  const lowestPricedGoatCleanVariant = getSelectedSizeVariant(
    getFirstLowestPriceVariants(productVariants, [SHOE_CONDITIONS.GOAT_CLEAN]),
    size,
  );
  const newStock = getSelectedSizeVariant(
    getFirstLowestPriceVariants(productVariants, [
      SHOE_CONDITIONS.NEW_NO_DEFECTS,
      SHOE_CONDITIONS.NEW_WITH_DEFECTS,
    ]),
    size,
  );
  const usedStock = getSelectedSizeVariant(
    getFirstLowestPriceVariants(productVariants, [
      SHOE_CONDITIONS.USED,
      SHOE_CONDITIONS.GOAT_CLEAN,
    ]),
    size,
  );

  const sharedAttributes: Partial<IEventAttributes> = {
    event_context: 'buy_bar',
    product_template_slug: slug,
    sku,
    currency_code: productVariants?.[0]?.lowestPriceCents?.currency || retailPrice?.currency,
    size,
    ...(lowestPricedGoatCleanVariant && {
      used_clean_price: lowestPricedGoatCleanVariant?.lowestPriceCents?.amountUsdCents?.toString(),
    }),
    ...(lowestPricedUsedVariant && {
      used_price: lowestPricedUsedVariant?.lowestPriceCents?.amountUsdCents?.toString(),
    }),
    ...(instantShipVariant && {
      new_instant_price:
        instantShipVariant?.instantShipLowestPriceCents?.amountUsdCents?.toString(),
    }),
    ...(lowestPricedNewBadBoxCondition && {
      new_box_issue_price:
        lowestPricedNewBadBoxCondition?.lowestPriceCents?.amountUsdCents?.toString(),
    }),
  };

  switch (eventName) {
    case 'PDP_PRICE_SIZE_CAROUSEL_TAP':
    case 'PDP_PRICE_SIZE_SELECTION_TAP':
      sendTrackingEvent(eventName, {
        ...sharedAttributes,
        under_retail: isUnderRetail,
        instant_ship_available: !isEmpty(instantShipVariant),
        ...(lowestPricedNewGoodBoxCondition && {
          new_best_price:
            lowestPricedNewGoodBoxCondition?.lowestPriceCents?.amountUsdCents?.toString(),
          price: lowestPricedNewGoodBoxCondition?.lowestPriceCents?.amountUsdCents?.toString(),
        }),
        ...(lowestPricedNewNoBoxCondition && {
          new_no_box_price:
            lowestPricedNewNoBoxCondition?.lowestPriceCents?.amountUsdCents?.toString(),
        }),
        ...(lowestPricedNewMissingLidCondition && {
          new_missing_lid_price:
            lowestPricedNewMissingLidCondition?.lowestPriceCents?.amountUsdCents?.toString(),
        }),
        ...(lowestPricedNewWithDefects && {
          new_with_defects_price:
            lowestPricedNewWithDefects?.lowestPriceCents?.amountUsdCents?.toString(),
        }),
        service_name: service_name || 'constructor',
      });
      break;
    case 'CART_ADD':
      sendTrackingEvent('CART_ADD', {
        ...sharedAttributes,
        under_retail: isUnderRetail,
        instant_ship_available: !isEmpty(instantShipVariant),
        is_apparel_product: isApparelProduct,
        ...(lowestPricedNewGoodBoxCondition && {
          new_best_price:
            lowestPricedNewGoodBoxCondition?.lowestPriceCents?.amountUsdCents?.toString(),
          price: lowestPricedNewGoodBoxCondition?.lowestPriceCents?.amountUsdCents?.toString(),
        }),
        ...(lowestPricedNewNoBoxCondition && {
          new_no_box_price:
            lowestPricedNewNoBoxCondition?.lowestPriceCents?.amountUsdCents?.toString(),
        }),
        ...(lowestPricedNewMissingLidCondition && {
          new_missing_lid_price:
            lowestPricedNewMissingLidCondition?.lowestPriceCents?.amountUsdCents?.toString(),
        }),
        ...(lowestPricedNewWithDefects && {
          new_with_defects_price:
            lowestPricedNewWithDefects?.lowestPriceCents?.amountUsdCents?.toString(),
        }),
        ...(clientDedupID && { client_dedup_id: clientDedupID }),
        service_name: service_name || 'constructor',
      });
      break;
    case 'PDP_BUY_BAR_VIEW':
      sendTrackingEvent('PDP_BUY_BAR_VIEW', {
        ...sharedAttributes,
        section,
        new_stock: !isEmpty(newStock),
        used_stock: !isEmpty(usedStock),
        expanded,
        ...(lowestPricedNewGoodBoxCondition && {
          new_standard_price:
            lowestPricedNewGoodBoxCondition?.lowestPriceCents?.amountUsdCents?.toString(),
          price: lowestPricedNewGoodBoxCondition?.lowestPriceCents?.amountUsdCents?.toString(),
          standard_last_sale:
            lowestPricedNewGoodBoxCondition?.lastSoldPriceCents?.amountUsdCents?.toString(),
          under_retail:
            lowestPricedNewGoodBoxCondition?.lowestPriceCents?.amountUsdCents <
            retailPrice?.amountUsdCents,
        }),
        ...(lowestPricedNewWithDefects && {
          new_w_defects_price:
            lowestPricedNewWithDefects?.lowestPriceCents?.amountUsdCents?.toString(),
        }),
        ...(lowestPricedBadBoxCondition && {
          box_issue_last_sale:
            lowestPricedBadBoxCondition?.lastSoldPriceCents?.amountUsdCents?.toString(),
          service_name: service_name || 'constructor',
        }),
      });
      break;
    default:
      // Do nothing
      break;
  }
};
